import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { theme } from 'antd';
import api from '../services/api';

export interface ThemeConfig {
  id?: number;
  name: string;
  isDefault: boolean;
  isActive: boolean;
  variables: {
    // 颜色系统
    primaryColor: string;
    secondaryColor: string;
    successColor: string;
    warningColor: string;
    errorColor: string;
    infoColor: string;
    
    // 背景色
    backgroundColor: string;
    surfaceColor: string;
    cardColor: string;
    borderColor: string;
    
    // 文字颜色
    textColor: string;
    textSecondaryColor: string;
    textDisabledColor: string;
    accentColor?: string;
    hoverColor?: string;
    activeColor?: string;
    
    // 透明度
    backgroundOpacity?: number;
    cardOpacity?: number;
    borderOpacity?: number;
    
    // 背景图片
    backgroundImage?: string;
    backgroundSize?: string;
    backgroundPosition?: string;
    backgroundRepeat?: string;
    
    // 布局
    borderRadius: string;
    cardBorderRadius?: string;
    buttonBorderRadius?: string;
    boxShadow: string;
    cardShadow?: string;
    spacing: string;
    padding?: string;
    margin?: string;
    lineHeight?: string;
    
    // 渐变
    gradientPrimary: string;
    gradientSecondary: string;
    gradientBackground: string;
    
    // 动画
    transitionDuration: string;
    transitionTiming: string;
    
    // 字体
    fontFamily: string;
    fontSize: string;
    fontWeight: string;
    
    // 移动端
    mobileBreakpoint: string;
    tabletBreakpoint: string;
    
    // 头部和导航条
    headerBackgroundColor?: string;
    headerTextColor?: string;
    headerHeight?: string;
    headerPadding?: string;
    headerBoxShadow?: string;
    headerBorderBottom?: string;
    headerOpacity?: number;
    
    // 导航条
    navBackgroundColor?: string;
    navTextColor?: string;
    navHoverColor?: string;
    navActiveColor?: string;
    navHeight?: string;
    navPadding?: string;
    navBorderRadius?: string;
    navBoxShadow?: string;
    navOpacity?: number;
    
    // Logo
    logoSize?: string;
    logoColor?: string;
    logoHoverColor?: string;
    logoUrl?: string;
    logoWidth?: string;
    logoHeight?: string;
    
    // 菜单项
    menuItemPadding?: string;
    menuItemBorderRadius?: string;
    menuItemHoverBackground?: string;
    menuItemActiveBackground?: string;
    menuItemTextColor?: string;
    menuItemHoverTextColor?: string;
    menuItemActiveTextColor?: string;
    
    // 价格表专用变量
    pricingTableHeaderBg?: string;
    pricingTableHeaderHoverBg?: string;
    pricingTableHeaderAccent?: string;
    pricingTablePriceColor?: string;
    pricingTableDiscountColor?: string;
    pricingTableDiscountBg?: string;
    pricingTableGameIconShadow?: string;
    pricingTableFooterBg?: string;
    
    // 自定义CSS
    customCSS: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

interface ThemeContextType {
  currentTheme: ThemeConfig | null;
  themes: ThemeConfig[];
  loading: boolean;
  antdTheme?: any;
  setTheme: (themeId: number) => Promise<void>;
  createTheme: (theme: Omit<ThemeConfig, 'id'>) => Promise<void>;
  updateTheme: (themeId: number, theme: Partial<ThemeConfig>) => Promise<void>;
  deleteTheme: (themeId: number) => Promise<void>;
  setDefaultTheme: (themeId: number) => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeConfig | null>(null);
  const [themes, setThemes] = useState<ThemeConfig[]>([]);
  const [loading, setLoading] = useState(true);

  // 加载主题列表
  const loadThemes = useCallback(async () => {
    try {
      setLoading(true);
      
      // 检查当前是否在公开页面
      const publicPaths = ['/', '/pricing'];
      const isPublicPath = publicPaths.includes(window.location.pathname) || 
                          window.location.pathname.startsWith('/share/');
      
      if (isPublicPath) {
        console.log('🔓 公开页面，使用默认主题');
        // 使用默认主题
        const defaultTheme = createDefaultTheme();
        setCurrentTheme(defaultTheme);
        applyTheme(defaultTheme);
        setLoading(false);
        return;
      }
      
      console.log('🔄 加载主题列表...');
      const response = await api.get('/admin/themes');
      console.log('📋 主题列表:', response.data);
      setThemes(response.data);
      
      // 找到当前激活的主题
      const activeTheme = response.data.find((theme: ThemeConfig) => theme.isActive);
      if (activeTheme) {
        console.log('✅ 找到激活主题:', activeTheme.name);
        setCurrentTheme(activeTheme);
        applyTheme(activeTheme);
      } else {
        console.log('⚠️ 没有找到激活主题，使用默认主题');
        const defaultTheme = createDefaultTheme();
        setCurrentTheme(defaultTheme);
        applyTheme(defaultTheme);
      }
    } catch (error) {
      console.error('❌ 加载主题失败:', error);
      // 使用默认主题
      const defaultTheme = createDefaultTheme();
      setCurrentTheme(defaultTheme);
      applyTheme(defaultTheme);
    } finally {
      setLoading(false);
    }
  }, []);

  // 组件挂载时加载主题
  useEffect(() => {
    console.log('🔄 组件挂载，开始加载主题');
    loadThemes();
  }, []); // 移除loadThemes依赖，避免无限循环

  // 监听路径变化，当从公开页面切换到认证页面时重新加载主题
  useEffect(() => {
    const handlePathChange = () => {
      const publicPaths = ['/', '/pricing'];
      const isPublicPath = publicPaths.includes(window.location.pathname) || 
                          window.location.pathname.startsWith('/share/');
      
      // 如果当前不是公开页面，且还没有加载过主题列表，则加载主题
      if (!isPublicPath && themes.length === 0) {
        console.log('🔄 路径变化到认证页面，重新加载主题');
        loadThemes();
      }
    };

    // 监听 popstate 事件（浏览器前进后退）
    window.addEventListener('popstate', handlePathChange);
    
    // 定期检查路径变化（因为 React Router 可能不会触发 popstate）
    const interval = setInterval(handlePathChange, 1000);
    
    return () => {
      window.removeEventListener('popstate', handlePathChange);
      clearInterval(interval);
    };
  }, [themes.length, loadThemes]);

  // 创建默认主题
  const createDefaultTheme = (): ThemeConfig => ({
    id: 0,
    name: '默认主题',
    isDefault: true,
    isActive: true,
    variables: {
      primaryColor: '#1890ff',
      secondaryColor: '#722ed1',
      successColor: '#52c41a',
      warningColor: '#faad14',
      errorColor: '#ff4d4f',
      infoColor: '#1890ff',
      backgroundColor: '#f5f5f5',
      surfaceColor: '#ffffff',
      cardColor: '#ffffff',
      borderColor: '#d9d9d9',
      textColor: '#262626',
      textSecondaryColor: '#8c8c8c',
      textDisabledColor: '#bfbfbf',
      borderRadius: '6px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      spacing: '16px',
      gradientPrimary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      gradientSecondary: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      gradientBackground: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      transitionDuration: '0.3s',
      transitionTiming: 'cubic-bezier(0.4, 0, 0.2, 1)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: '14px',
      fontWeight: '400',
      mobileBreakpoint: '768px',
      tabletBreakpoint: '1024px',
      customCSS: ''
    }
  });

  // 应用主题
  const applyTheme = (theme: ThemeConfig) => {
    console.log('🎨 应用主题:', theme.name, theme.variables);
    const root = document.documentElement;
    const variables = theme.variables;
    
    // 强制清除所有之前的主题变量，确保重新设置
    const allVariableKeys = [
      '--primary-color', '--secondary-color', '--success-color', '--warning-color', '--error-color', '--info-color',
      '--background-color', '--surface-color', '--card-color', '--border-color', '--text-color', '--text-secondary-color', '--text-disabled-color',
      '--accent-color', '--hover-color', '--active-color', '--background-opacity', '--card-opacity', '--border-opacity',
      '--header-opacity', '--nav-opacity', '--font-family', '--font-size', '--font-weight', '--line-height',
      '--spacing', '--padding', '--margin', '--transition-duration', '--transition-timing',
      '--border-radius', '--box-shadow', '--card-border-radius', '--button-border-radius', '--card-shadow',
      '--gradient-primary', '--gradient-secondary', '--gradient-background', '--gradient-direction',
      '--header-background-color', '--header-text-color', '--header-height', '--header-padding', '--header-box-shadow', '--header-border-bottom',
      '--nav-background-color', '--nav-text-color', '--nav-hover-color', '--nav-active-color', '--nav-height', '--nav-padding', '--nav-border-radius', '--nav-box-shadow',
      '--logo-size', '--logo-color', '--logo-hover-color',
      '--menu-item-padding', '--menu-item-border-radius', '--menu-item-hover-background', '--menu-item-active-background',
      '--menu-item-text-color', '--menu-item-hover-text-color', '--menu-item-active-text-color',
      '--pricing-table-header-bg', '--pricing-table-header-hover-bg', '--pricing-table-header-accent',
      '--pricing-table-price-color', '--pricing-table-discount-color', '--pricing-table-discount-bg',
      '--pricing-table-game-icon-shadow', '--pricing-table-footer-bg'
    ];
    
    allVariableKeys.forEach(key => {
      root.style.removeProperty(key);
    });
    
    // 设置基础CSS变量 - 使用更精确的映射
    const variableMappings = {
      // 基本颜色
      primaryColor: '--primary-color',
      secondaryColor: '--secondary-color',
      successColor: '--success-color',
      warningColor: '--warning-color',
      errorColor: '--error-color',
      infoColor: '--info-color',
      backgroundColor: '--background-color',
      surfaceColor: '--surface-color',
      cardColor: '--card-color',
      borderColor: '--border-color',
      textColor: '--text-color',
      textSecondaryColor: '--text-secondary-color',
      textDisabledColor: '--text-disabled-color',
      accentColor: '--accent-color',
      hoverColor: '--hover-color',
      activeColor: '--active-color',
      
      // 透明度
      backgroundOpacity: '--background-opacity',
      cardOpacity: '--card-opacity',
      borderOpacity: '--border-opacity',
      headerOpacity: '--header-opacity',
      navOpacity: '--nav-opacity',
      
      // 背景图片 - 不在这里设置，由特殊处理逻辑处理
      
      // 字体
      fontFamily: '--font-family',
      fontSize: '--font-size',
      fontWeight: '--font-weight',
      lineHeight: '--line-height',
      
      // 间距
      spacing: '--spacing',
      padding: '--padding',
      margin: '--margin',
      
      // 动画
      transitionDuration: '--transition-duration',
      transitionTiming: '--transition-timing',
      
      // 边框和阴影
      borderRadius: '--border-radius',
      boxShadow: '--box-shadow',
      cardBorderRadius: '--card-border-radius',
      buttonBorderRadius: '--button-border-radius',
      cardShadow: '--card-shadow',
      
      // 渐变
      gradientPrimary: '--gradient-primary',
      gradientSecondary: '--gradient-secondary',
      gradientBackground: '--gradient-background',
      gradientDirection: '--gradient-direction',
      
      // 头部导航
      headerBackgroundColor: '--header-background-color',
      headerTextColor: '--header-text-color',
      headerHeight: '--header-height',
      headerPadding: '--header-padding',
      headerBoxShadow: '--header-box-shadow',
      headerBorderBottom: '--header-border-bottom',
      
      // 导航栏
      navBackgroundColor: '--nav-background-color',
      navTextColor: '--nav-text-color',
      navHoverColor: '--nav-hover-color',
      navActiveColor: '--nav-active-color',
      navHeight: '--nav-height',
      navPadding: '--nav-padding',
      navBorderRadius: '--nav-border-radius',
      navBoxShadow: '--nav-box-shadow',
      
      // Logo
      logoSize: '--logo-size',
      logoColor: '--logo-color',
      logoHoverColor: '--logo-hover-color',
      
      // 菜单项
      menuItemPadding: '--menu-item-padding',
      menuItemBorderRadius: '--menu-item-border-radius',
      menuItemHoverBackground: '--menu-item-hover-background',
      menuItemActiveBackground: '--menu-item-active-background',
      menuItemTextColor: '--menu-item-text-color',
      menuItemHoverTextColor: '--menu-item-hover-text-color',
      menuItemActiveTextColor: '--menu-item-active-text-color',
      
      // 价格表专用变量映射
      pricingTableHeaderBg: '--pricing-table-header-bg',
      pricingTableHeaderHoverBg: '--pricing-table-header-hover-bg',
      pricingTableHeaderAccent: '--pricing-table-header-accent',
      pricingTablePriceColor: '--pricing-table-price-color',
      pricingTableDiscountColor: '--pricing-table-discount-color',
      pricingTableDiscountBg: '--pricing-table-discount-bg',
      pricingTableGameIconShadow: '--pricing-table-game-icon-shadow',
      pricingTableFooterBg: '--pricing-table-footer-bg'
    };
    
        // 设置所有映射的变量
        Object.entries(variableMappings).forEach(([key, cssVar]) => {
          if (variables[key as keyof typeof variables] !== undefined && variables[key as keyof typeof variables] !== null) {
            let value = String(variables[key as keyof typeof variables]);
            
            // 透明度验证和修正
            if (key.includes('Opacity')) {
              const opacityValue = parseFloat(value);
              if (isNaN(opacityValue) || opacityValue < 0.1) {
                // 如果透明度小于0.1或无效，设置为默认值
                const defaultOpacity = key === 'cardOpacity' ? 1.0 : 
                                     key === 'backgroundOpacity' ? 0.95 : 
                                     key === 'borderOpacity' ? 0.8 : 1.0;
                value = String(defaultOpacity);
                console.log(`🔧 修正透明度: ${key} 从 ${variables[key as keyof typeof variables]} 改为 ${value}`);
              }
            }
            
            root.style.setProperty(cssVar, value);
            console.log(`设置CSS变量: ${cssVar} = ${value}`);

            // 特别关注边框和阴影变量
            if (['borderRadius', 'boxShadow', 'cardBorderRadius', 'buttonBorderRadius', 'cardShadow'].includes(key)) {
              console.log(`🎨 边框阴影变量: ${cssVar} = ${value}`);
            }
          }
        });
    
    // 智能生成悬停色和激活色
    const generateHoverActiveColors = () => {
      // 如果没有设置悬停色，基于主色调生成
      if (!variables.hoverColor && variables.primaryColor) {
        const hoverColor = adjustColorBrightness(variables.primaryColor, 0.1); // 稍微亮一点
        root.style.setProperty('--hover-color', hoverColor);
        console.log(`🎨 智能生成悬停色: ${hoverColor}`);
      }
      
      // 如果没有设置激活色，基于主色调生成
      if (!variables.activeColor && variables.primaryColor) {
        const activeColor = adjustColorBrightness(variables.primaryColor, 0.2); // 更亮一点
        root.style.setProperty('--active-color', activeColor);
        console.log(`🎨 智能生成激活色: ${activeColor}`);
      }
      
      // 如果没有设置菜单项悬停背景，基于表面色生成
      if (!variables.menuItemHoverBackground && variables.surfaceColor) {
        const menuHoverBg = adjustColorBrightness(variables.surfaceColor, 0.05); // 稍微亮一点
        root.style.setProperty('--menu-item-hover-background', menuHoverBg);
        console.log(`🎨 智能生成菜单悬停背景: ${menuHoverBg}`);
      }
      
      // 确保表格悬停色有合适的值
      if (!variables.menuItemHoverBackground) {
        const tableHoverBg = variables.surfaceColor ? 
          adjustColorBrightness(variables.surfaceColor, 0.05) : 
          '#f5f5f5';
        root.style.setProperty('--menu-item-hover-background', tableHoverBg);
        console.log(`🎨 智能生成表格悬停背景: ${tableHoverBg}`);
      }
      
      // 如果没有设置菜单项激活背景，基于主色调生成
      if (!variables.menuItemActiveBackground && variables.primaryColor) {
        const menuActiveBg = adjustColorBrightness(variables.primaryColor, 0.9); // 很淡的主色调
        root.style.setProperty('--menu-item-active-background', menuActiveBg);
        console.log(`🎨 智能生成菜单激活背景: ${menuActiveBg}`);
      }
    };
    
    // 颜色亮度调整函数
    const adjustColorBrightness = (color: string, factor: number): string => {
      // 处理渐变色
      if (color.includes('gradient')) {
        return color; // 渐变色直接返回
      }
      
      // 处理十六进制颜色
      if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        
        const newR = Math.min(255, Math.max(0, r + (255 - r) * factor));
        const newG = Math.min(255, Math.max(0, g + (255 - g) * factor));
        const newB = Math.min(255, Math.max(0, b + (255 - b) * factor));
        
        return `#${Math.round(newR).toString(16).padStart(2, '0')}${Math.round(newG).toString(16).padStart(2, '0')}${Math.round(newB).toString(16).padStart(2, '0')}`;
      }
      
      // 处理rgb颜色
      if (color.startsWith('rgb')) {
        const matches = color.match(/\d+/g);
        if (matches && matches.length >= 3) {
          const r = parseInt(matches[0]);
          const g = parseInt(matches[1]);
          const b = parseInt(matches[2]);
          
          const newR = Math.min(255, Math.max(0, r + (255 - r) * factor));
          const newG = Math.min(255, Math.max(0, g + (255 - g) * factor));
          const newB = Math.min(255, Math.max(0, b + (255 - b) * factor));
          
          return `rgb(${Math.round(newR)}, ${Math.round(newG)}, ${Math.round(newB)})`;
        }
      }
      
      return color; // 无法处理的颜色直接返回
    };
    
    // 执行智能颜色生成
    generateHoverActiveColors();
    
    // 特殊处理背景图片（需要组合多个属性）
    
    // 强制确保边框和阴影变量被正确设置
    const forceSetBorderShadow = () => {
      if (variables.borderRadius) {
        root.style.setProperty('--border-radius', variables.borderRadius);
        console.log('🔧 强制设置边框圆角:', variables.borderRadius);
      }
      if (variables.boxShadow) {
        root.style.setProperty('--box-shadow', variables.boxShadow);
        console.log('🔧 强制设置通用阴影:', variables.boxShadow);
      }
      if (variables.cardBorderRadius) {
        root.style.setProperty('--card-border-radius', variables.cardBorderRadius);
        console.log('🔧 强制设置卡片圆角:', variables.cardBorderRadius);
      }
      if (variables.buttonBorderRadius) {
        root.style.setProperty('--button-border-radius', variables.buttonBorderRadius);
        console.log('🔧 强制设置按钮圆角:', variables.buttonBorderRadius);
      }
      if (variables.cardShadow) {
        root.style.setProperty('--card-shadow', variables.cardShadow);
        console.log('🔧 强制设置卡片阴影:', variables.cardShadow);
      }
    };
    
    // 延迟执行，确保其他设置完成后再应用
    setTimeout(forceSetBorderShadow, 100);
    
    // 背景图片特殊处理 - 需要添加url()包装
    if (variables.backgroundImage) {
      const backgroundImage = `url("${variables.backgroundImage}")`;
      const backgroundSize = variables.backgroundSize || 'cover';
      const backgroundPosition = variables.backgroundPosition || 'center';
      const backgroundRepeat = variables.backgroundRepeat || 'no-repeat';
      
      // 设置背景图片相关变量
      root.style.setProperty('--background-image', backgroundImage);
      root.style.setProperty('--background-size', backgroundSize);
      root.style.setProperty('--background-position', backgroundPosition);
      root.style.setProperty('--background-repeat', backgroundRepeat);
      
      console.log(`🎨 设置背景图片: ${backgroundImage}`);
      console.log(`🎨 背景图片尺寸: ${backgroundSize}`);
      console.log(`🎨 背景图片位置: ${backgroundPosition}`);
      console.log(`🎨 背景图片重复: ${backgroundRepeat}`);
      
      // 强制应用背景图片到body元素和html元素
      setTimeout(() => {
        const body = document.body;
        const html = document.documentElement;
        
        if (body) {
          body.style.backgroundImage = backgroundImage;
          body.style.backgroundSize = backgroundSize;
          body.style.backgroundPosition = backgroundPosition;
          body.style.backgroundRepeat = backgroundRepeat;
          body.style.backgroundAttachment = 'fixed';
          console.log('🔧 强制应用背景图片到body元素');
        }
        
        if (html) {
          html.style.backgroundImage = backgroundImage;
          html.style.backgroundSize = backgroundSize;
          html.style.backgroundPosition = backgroundPosition;
          html.style.backgroundRepeat = backgroundRepeat;
          html.style.backgroundAttachment = 'fixed';
          console.log('🔧 强制应用背景图片到html元素');
        }
        
        // 确保背景图片不被其他样式覆盖
        const style = document.createElement('style');
        style.id = 'force-background-image';
        style.textContent = `
          body, html {
            background-image: ${backgroundImage} !important;
            background-size: ${backgroundSize} !important;
            background-position: ${backgroundPosition} !important;
            background-repeat: ${backgroundRepeat} !important;
            background-attachment: fixed !important;
          }
        `;
        
        // 移除旧的强制样式
        const oldStyle = document.getElementById('force-background-image');
        if (oldStyle) {
          oldStyle.remove();
        }
        
        document.head.appendChild(style);
        console.log('🔧 添加强制背景图片样式');
      }, 100);
    } else {
      root.style.removeProperty('--background-image');
      root.style.removeProperty('--background-size');
      root.style.removeProperty('--background-position');
      root.style.removeProperty('--background-repeat');
      
      // 清除body和html元素的背景图片
      setTimeout(() => {
        const body = document.body;
        const html = document.documentElement;
        
        if (body) {
          body.style.backgroundImage = '';
          body.style.backgroundSize = '';
          body.style.backgroundPosition = '';
          body.style.backgroundRepeat = '';
          body.style.backgroundAttachment = '';
          console.log('🔧 清除body元素的背景图片');
        }
        
        if (html) {
          html.style.backgroundImage = '';
          html.style.backgroundSize = '';
          html.style.backgroundPosition = '';
          html.style.backgroundRepeat = '';
          html.style.backgroundAttachment = '';
          console.log('🔧 清除html元素的背景图片');
        }
        
        // 移除强制背景图片样式
        const oldStyle = document.getElementById('force-background-image');
        if (oldStyle) {
          oldStyle.remove();
          console.log('🔧 移除强制背景图片样式');
        }
      }, 100);
      
      console.log('🎨 清除背景图片');
    }
    
    // 特殊处理背景图片（需要组合多个属性）
    
    // 应用自定义CSS
    if (variables.customCSS) {
      let styleElement = document.getElementById('theme-custom-css');
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = 'theme-custom-css';
        document.head.appendChild(styleElement);
      }
      styleElement.textContent = variables.customCSS;
      // 只在调试模式下显示完整CSS，否则只显示摘要
      const debugMode = localStorage.getItem('debugMode') === 'true';
      if (debugMode) {
        console.log('🎨 应用自定义CSS:', variables.customCSS);
      } else {
        console.log('🎨 应用自定义CSS:', `${variables.customCSS.length} 字符`);
      }
    } else {
      // 清除自定义CSS
      const styleElement = document.getElementById('theme-custom-css');
      if (styleElement) {
        styleElement.remove();
      }
      console.log('🎨 清除自定义CSS');
    }
  };

  // 设置主题
  const setTheme = async (themeId: number) => {
    try {
      await api.post(`/admin/themes/${themeId}/activate`);
      
      // 重新加载主题列表以获取最新状态
      const response = await api.get('/admin/themes');
      setThemes(response.data);
      
      // 找到并设置当前主题
      const activeTheme = response.data.find((theme: ThemeConfig) => theme.isActive);
      if (activeTheme) {
        setCurrentTheme(activeTheme);
        applyTheme(activeTheme);
      }
    } catch (error) {
      console.error('设置主题失败:', error);
    }
  };

  // 数据完整性检查函数
  const checkDataIntegrity = (originalData: any, processedData: any, operation: string) => {
    const criticalFields = [
      'textColor', 'primaryColor', 'backgroundColor', 'surfaceColor', 'cardColor',
      'borderRadius', 'boxShadow', 'cardBorderRadius', 'buttonBorderRadius', 'cardShadow',
      'backgroundImage', 'backgroundSize', 'backgroundPosition', 'backgroundRepeat'
    ];
    
    const lostFields: string[] = [];
    const changedFields: Array<{ field: string; original: any; processed: any }> = [];
    
    criticalFields.forEach(field => {
      const original = originalData.variables?.[field];
      const processed = processedData.variables?.[field];
      
      if (original !== undefined && processed === undefined) {
        lostFields.push(field);
      } else if (original !== processed && original !== undefined) {
        changedFields.push({ field, original, processed });
      }
    });
    
    if (lostFields.length > 0) {
      console.error(`❌ ${operation}时丢失了字段:`, lostFields);
    }
    
    if (changedFields.length > 0) {
      console.warn(`⚠️ ${operation}时字段被修改:`, changedFields);
    }
    
    if (lostFields.length === 0 && changedFields.length === 0) {
      console.log(`✅ ${operation}数据完整性检查通过`);
    }
    
    return { lostFields, changedFields };
  };

  // 统一的主题数据验证和修正函数
  const validateAndFixThemeData = (themeData: any, isUpdate: boolean = false) => {
    if (!themeData.variables) {
      console.warn('⚠️ 主题数据中没有variables字段');
      return themeData;
    }

    const originalData = { ...themeData };
    const variables = { ...themeData.variables };
    let hasChanges = false;

    // 透明度验证和修正 - 只在值无效时修正
    if (variables.cardOpacity !== undefined) {
      const opacity = parseFloat(String(variables.cardOpacity));
      if (isNaN(opacity) || opacity < 0.1) {
        variables.cardOpacity = 1.0;
        hasChanges = true;
        console.log('🔧 修正卡片透明度为1.0');
      }
    }
    
    if (variables.backgroundOpacity !== undefined) {
      const opacity = parseFloat(String(variables.backgroundOpacity));
      if (isNaN(opacity) || opacity < 0.1) {
        variables.backgroundOpacity = 0.95;
        hasChanges = true;
        console.log('🔧 修正背景透明度为0.95');
      }
    }
    
    if (variables.borderOpacity !== undefined) {
      const opacity = parseFloat(String(variables.borderOpacity));
      if (isNaN(opacity) || opacity < 0.1) {
        variables.borderOpacity = 0.8;
        hasChanges = true;
        console.log('🔧 修正边框透明度为0.8');
      }
    }

    if (variables.headerOpacity !== undefined) {
      const opacity = parseFloat(String(variables.headerOpacity));
      if (isNaN(opacity) || opacity < 0.1) {
        variables.headerOpacity = 1.0;
        hasChanges = true;
        console.log('🔧 修正头部透明度为1.0');
      }
    }

    if (variables.navOpacity !== undefined) {
      const opacity = parseFloat(String(variables.navOpacity));
      if (isNaN(opacity) || opacity < 0.1) {
        variables.navOpacity = 1.0;
        hasChanges = true;
        console.log('🔧 修正导航透明度为1.0');
      }
    }

    const processedData = {
      ...themeData,
      variables
    };

    // 数据完整性检查
    checkDataIntegrity(originalData, processedData, isUpdate ? '更新' : '创建');

    if (hasChanges) {
      console.log('🔧 主题数据已修正:', { 
        cardOpacity: variables.cardOpacity,
        backgroundOpacity: variables.backgroundOpacity,
        borderOpacity: variables.borderOpacity,
        headerOpacity: variables.headerOpacity,
        navOpacity: variables.navOpacity
      });
    }

    return processedData;
  };

  // 创建主题
  const createTheme = async (theme: Omit<ThemeConfig, 'id'>) => {
    try {
      console.log('💾 创建主题:', theme);
      
      // 验证和修正主题数据
      const validatedTheme = validateAndFixThemeData(theme, false);
      
      const response = await api.post('/admin/themes', validatedTheme);
      setThemes(prev => [...prev, response.data]);
      console.log('✅ 主题创建成功:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('创建主题失败:', error);
      throw error;
    }
  };

  // 更新主题
  const updateTheme = async (themeId: number, themeData: Partial<ThemeConfig>) => {
    try {
      console.log('💾 更新主题:', themeId, themeData);
      
      // 验证和修正主题数据
      const validatedThemeData = validateAndFixThemeData(themeData, true);
      
      console.log('🔍 关键变量检查:', {
        textColor: validatedThemeData.variables?.textColor,
        primaryColor: validatedThemeData.variables?.primaryColor,
        backgroundColor: validatedThemeData.variables?.backgroundColor,
        borderRadius: validatedThemeData.variables?.borderRadius,
        boxShadow: validatedThemeData.variables?.boxShadow,
        backgroundImage: validatedThemeData.variables?.backgroundImage,
        cardOpacity: validatedThemeData.variables?.cardOpacity,
        backgroundOpacity: validatedThemeData.variables?.backgroundOpacity,
        borderOpacity: validatedThemeData.variables?.borderOpacity,
      });
      
      const response = await api.put(`/admin/themes/${themeId}`, validatedThemeData);
      console.log('✅ 主题更新成功:', response.data);
      
      // 验证保存的完整性
      const savedTheme = response.data;
      const criticalVars = [
        'textColor', 'primaryColor', 'backgroundColor', 'surfaceColor', 'cardColor',
        'borderRadius', 'boxShadow', 'cardBorderRadius', 'buttonBorderRadius', 'cardShadow',
        'cardOpacity', 'backgroundOpacity', 'borderOpacity', 'headerOpacity', 'navOpacity',
        'backgroundImage', 'backgroundSize', 'backgroundPosition', 'backgroundRepeat'
      ];
      
      const missingVars = criticalVars.filter(varName => 
        !savedTheme.variables || savedTheme.variables[varName] === undefined
      );
      
      if (missingVars.length > 0) {
        console.warn('⚠️ 主题保存后缺少关键变量:', missingVars);
      } else {
        console.log('✅ 主题保存完整，所有关键变量都已保存');
      }
      
      // 更新本地主题列表
      setThemes(prev => prev.map(t => t.id === themeId ? response.data : t));
      
      // 如果更新的是当前主题，立即应用
      if (currentTheme?.id === themeId) {
        console.log('🔄 应用更新后的主题');
        setCurrentTheme(response.data);
        applyTheme(response.data);
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ 更新主题失败:', error);
      throw error;
    }
  };

  // 删除主题
  const deleteTheme = async (themeId: number) => {
    try {
      await api.delete(`/admin/themes/${themeId}`);
      setThemes(prev => prev.filter(t => t.id !== themeId));
      
      if (currentTheme?.id === themeId) {
        const defaultTheme = themes.find(t => t.isDefault && t.id !== themeId);
        if (defaultTheme) {
          setCurrentTheme(defaultTheme);
          applyTheme(defaultTheme);
        }
      }
    } catch (error) {
      console.error('删除主题失败:', error);
    }
  };

  // 设置默认主题
  const setDefaultTheme = async (themeId: number) => {
    try {
      await api.post(`/admin/themes/${themeId}/set-default`);
      
      // 重新加载主题列表以获取最新状态
      const response = await api.get('/admin/themes');
      setThemes(response.data);
    } catch (error) {
      console.error('设置默认主题失败:', error);
    }
  };

  // 移除重复的useEffect，避免无限循环

  // 生成Ant Design主题配置
  const antdTheme = currentTheme && currentTheme.variables ? {
    token: {
      colorPrimary: currentTheme.variables.primaryColor || '#1890ff',
      colorSuccess: currentTheme.variables.successColor || '#52c41a',
      colorWarning: currentTheme.variables.warningColor || '#faad14',
      colorError: currentTheme.variables.errorColor || '#ff4d4f',
      colorInfo: currentTheme.variables.infoColor || '#1890ff',
      colorBgContainer: currentTheme.variables.surfaceColor || '#ffffff',
      colorBgElevated: currentTheme.variables.cardColor || '#ffffff',
      colorBgLayout: currentTheme.variables.backgroundColor || '#f5f5f5',
      colorBorder: currentTheme.variables.borderColor || '#d9d9d9',
      colorText: currentTheme.variables.textColor || '#262626',
      colorTextSecondary: currentTheme.variables.textSecondaryColor || '#8c8c8c',
      colorTextTertiary: currentTheme.variables.textDisabledColor || '#bfbfbf',
      borderRadius: currentTheme.variables.borderRadius ? 
        parseFloat(currentTheme.variables.borderRadius.replace('px', '')) || 6 : 6,
      fontFamily: currentTheme.variables.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: currentTheme.variables.fontSize ? 
        parseFloat(currentTheme.variables.fontSize.replace('px', '')) || 14 : 14,
      boxShadow: currentTheme.variables.boxShadow || '0 2px 8px rgba(0, 0, 0, 0.15)',
    },
    components: {
      Button: {
        colorPrimary: currentTheme.variables.primaryColor || '#1890ff',
        // 悬停和激活颜色通过CSS变量处理，支持渐变色
      },
      Menu: {
        itemBg: 'transparent',
        itemColor: currentTheme.variables.menuItemTextColor || currentTheme.variables.textColor || '#262626',
        // 悬停和激活颜色通过CSS变量处理，支持渐变色
      },
      Card: {
        colorBgContainer: currentTheme.variables.cardColor || '#ffffff',
        colorBorderSecondary: currentTheme.variables.borderColor || '#d9d9d9',
      },
      Table: {
        colorBgContainer: currentTheme.variables.surfaceColor || '#ffffff',
        colorBorderSecondary: currentTheme.variables.borderColor || '#d9d9d9',
        colorText: currentTheme.variables.textColor || '#262626',
        colorTextSecondary: currentTheme.variables.textSecondaryColor || '#8c8c8c',
      },
    },
    algorithm: theme.defaultAlgorithm,
  } : undefined;

  const value: ThemeContextType = {
    currentTheme,
    themes,
    loading,
    antdTheme,
    setTheme,
    createTheme,
    updateTheme,
    deleteTheme,
    setDefaultTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
